import { personDisplayName, type SelectedItemRef } from "@/lib/selection";
import { createAuthenticatedApi } from "@/lib/utils";
import { type SearchQueryRequest, StudentsApi } from "@/openapi/generated";
import { toStudentSearchBody } from "./studentFilters";

const FETCH_PAGE_SIZE = 100;
const MAX_PAGES = 200;

export interface FetchAllMatchingStudentIdsParams {
  accessToken: string;
  active?: boolean;
  searchQueryRequest: SearchQueryRequest | null;
}

function toMatchingStudentRef(item: {
  studentId?: number | null;
  firstName?: string | null;
  lastName?: string | null;
}): SelectedItemRef | null {
  const id = Number(item.studentId);
  if (!Number.isFinite(id) || id <= 0) return null;
  return {
    id,
    name: personDisplayName(id, item.firstName, item.lastName, "Student"),
  };
}

export async function fetchAllMatchingStudents({
  accessToken,
  active,
  searchQueryRequest,
}: FetchAllMatchingStudentIdsParams): Promise<SelectedItemRef[]> {
  const service = createAuthenticatedApi(StudentsApi, accessToken);
  const searchBody = toStudentSearchBody(searchQueryRequest);

  const students: SelectedItemRef[] = [];
  const seen = new Set<number>();
  let page = 1;
  let lastSelectedId: number | undefined;
  let expectedTotal: number | null = null;

  while (page <= MAX_PAGES) {
    const data = searchBody
      ? await service.apiStudentsSearchPost({
          page,
          pageSize: FETCH_PAGE_SIZE,
          lastSelectedId,
          active,
          searchQueryRequest: searchBody,
        })
      : await service.apiStudentsScrollGet({
          page,
          pageSize: FETCH_PAGE_SIZE,
          lastSelectedId,
          active,
        });

    const items = data.items ?? [];
    if (expectedTotal == null && data.totalCount != null) {
      const parsed = Number(data.totalCount);
      expectedTotal = Number.isFinite(parsed) ? parsed : null;
    }

    for (const item of items) {
      const ref = toMatchingStudentRef({
        studentId: Number(item.studentId),
        firstName: item.firstName,
        lastName: item.lastName,
      });
      if (!ref || seen.has(ref.id)) continue;
      seen.add(ref.id);
      students.push(ref);
    }

    if (items.length === 0) break;
    if (expectedTotal != null && students.length >= expectedTotal) break;
    if (data.hasNextPage === false) break;

    const rawLastId = data.lastSelectedId;
    if (rawLastId == null) break;

    const parsedLastId =
      typeof rawLastId === "number" ? rawLastId : Number(rawLastId);
    if (!Number.isFinite(parsedLastId)) break;

    lastSelectedId = parsedLastId;
    page += 1;
  }

  return students;
}

export async function fetchAllMatchingStudentIds(
  params: FetchAllMatchingStudentIdsParams,
): Promise<number[]> {
  const students = await fetchAllMatchingStudents(params);
  return students.map((item) => item.id);
}
